import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import { contentSortIdToTranslationKey } from '@/lib/content-sort-options';
import { BEST_MATCHES_SORT_ID, isSamePathnameListingDataNavigationPending } from '@/lib/plp-panel-url-state';
import { CONTENT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import type { PlpSortingOption } from '@/components/eataly-category-plp-sticky-bar';

export function useConfiguredSorting(sortOptionIds: string[], defaultSortId?: string) {
    const { t } = useTranslation('categoryPlp');
    const location = useLocation();
    const navigation = useNavigation();
    const SORT = CONTENT_SEARCH_QUERY_PARAMS.SORT;
    const sortFromSearch = useCallback(
        (search: string): string | null => {
            const params = new URLSearchParams(search);
            return params.get(SORT) || null;
        },
        [SORT]
    );
    const isPending = useMemo(
        () =>
            isSamePathnameListingDataNavigationPending({
                pathname: location.pathname,
                search: location.search,
                navigationState: navigation.state,
                navigationLocation: navigation.location,
            }),
        [location.pathname, location.search, navigation.location, navigation.state]
    );
    const sortingOptions = useMemo<PlpSortingOption[]>(() => {
        const relevanceOption: PlpSortingOption = {
            id: BEST_MATCHES_SORT_ID,
            label: t('sortOptions.relevance'),
        };
        const configured = sortOptionIds
            .filter((id) => id !== BEST_MATCHES_SORT_ID)
            .map((id) => ({
                id,
                label: t(`contentSortOptions.${contentSortIdToTranslationKey(id)}`, { defaultValue: id }),
            }));
        return [relevanceOption, ...configured];
    }, [sortOptionIds, t]);

    const effectiveSortingOption = useMemo(() => {
        return (
            (navigation.location && navigation.location.pathname === location.pathname
                ? sortFromSearch(navigation.location.search)
                : sortFromSearch(location.search)) ??
            defaultSortId ??
            BEST_MATCHES_SORT_ID
        );
    }, [defaultSortId, location.pathname, location.search, navigation.location, sortFromSearch]);

    const selectedSortingOptionLabel = useMemo(() => {
        return (
            sortingOptions.find((option) => option.id === effectiveSortingOption)?.label ||
            sortingOptions[0]?.label ||
            ''
        );
    }, [effectiveSortingOption, sortingOptions]);

    return {
        isPending,
        effectiveSortingOption,
        selectedSortingOptionLabel,
        sortingOptions,
    };
}
